import { ArrowBack } from '@mui/icons-material';
import { Box, Container, Link, Paper, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { DashboardContext } from '../../contexts/DashboardContext';
import { FileStructureContext } from '../../contexts/FileStructureContext';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import { WorkspacesContext } from '../../contexts/WorkspacesContext';
import useBackwardsCompatible from '../../hooks/useBackwardsCompatible';
import useQueriesLoadedChecker from '../../hooks/useQueriesLoadedChecker';
import useRoute from '../../hooks/useRoute';
import { getDashboardById, getPublicDashboardById, getPublicWorkspaceFunc } from '../../lib/helper';
import { scrollbarStyle } from '../../shared/dashboard';
import { Workspace } from '../../types/files';
import storage from '../../utils/storage';
import DashboardLoading from '../molecules/dashboard-loading/DashboardLoading';
import CompanyDetailsCard from '../organisms/dashboard-details-v2/CompanyDetailsCard';
import CompanyHeader from '../organisms/dashboard-details-v2/CompanyHeader';
import TeamMembersCard from '../organisms/dashboard-details-v2/TeamMembersCard';
import TimelineCard from '../organisms/dashboard-details-v2/TimelineCard';
import DashboardAllowAccess from '../organisms/dashboard-details/DashboardAllowAccess';
import PublicDashboardNotFound from "../organisms/dashboard-details/DashboardNotFound";
import { FallbackLoading } from '../templates/loader';

type DashboardAccessType = {
    iss?: string | null,
    sub: string,
    exp: number | null,
};

const useStyles = makeStyles(() => ({
    container: {
        background: '#f5f7fa',
        minHeight: '100vh',
        padding: '24px 0',
        overflow: 'auto',
        ...scrollbarStyle,
    },
    link: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        padding: '8px 12px',
        color: '#6c757d',
        fontSize: 14,
        fontWeight: 500,
        borderRadius: 0.75,
        '&:hover': {
            color: '#1a8c8c',
            background: '#f8f9fa',
            cursor: 'pointer'
        },
    },
}));

const DashboardDetailsPageV2: React.FC<{
    publicDashboard?: boolean,
    sharedDashboard?: boolean
}> = ({ publicDashboard, sharedDashboard }) => {
    const classes = useStyles();
    const params: {[key: string]: string} = useParams();
    const { userGroup } = useContext(AuthContext);
    const { workspaces } = useContext(WorkspacesContext);
    const { initialLoading } = useContext(FileStructureContext);
    const { workspace, setWorkspace } = useContext(WorkspaceContext);
    const { dashboard, setDashboard, setPublicView, setShared } = useContext(DashboardContext);
    const { queriesLoaded } = useQueriesLoadedChecker();
    const { getURLFrom, redirectFromDashboard } = useRoute();
    const { dashboardWorkspaces } = useBackwardsCompatible();

    const [loaded, setLoaded] = useState<boolean>(false);
    const [checkedAccess, setCheckedAccess] = useState<boolean>(false);
    const [hasPublicAccess, setWithPublicAccess] = useState<boolean>(false);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const isInvalidPublicDashboard = useMemo(() =>
        loaded && publicDashboard && (!dashboard || dashboard?.isPublic === false)
    , [loaded, publicDashboard, dashboard]);

    const isValidatingPublicDashboard = useMemo(() =>
        loaded && (!userGroup && publicDashboard) && (dashboard?.isPublic) && (!checkedAccess || !hasPublicAccess)
    , [checkedAccess, hasPublicAccess, loaded, publicDashboard, userGroup, dashboard?.isPublic]);

    const handleStoreAccess = useCallback((workspace?: Workspace) => {
        if (!workspace)
            return;

        const stream = storage.getItem('config.workspace-public');
        const issuer = workspace.email || '';
        const accessData: DashboardAccessType = {
            iss: issuer,
            sub: workspace.id,
            exp: null,
        };

        if (!!stream) {
            const accessList = (JSON.parse(atob(stream)) ?? []) as DashboardAccessType[];
            const lookUpAccess = accessList.find(access => (access.iss === issuer && access.sub === workspace.id));

            if (!lookUpAccess) {
                accessList.push(accessData);
                storage.setItem('config.workspace-public', btoa(JSON.stringify(accessList)));
            }
        } else {
            storage.setItem('config.workspace-public', btoa(JSON.stringify([accessData])));
        }
    }, []);

    const handleVerifyAccess = useCallback((workspace?: Workspace) => {
        if (!workspace)
            return;

        timerRef.current = setTimeout(() => {
            const stream = storage.getItem('config.workspace-public');
            const issuer = workspace.email || '';

            if (!!stream) {
                const accessList = (JSON.parse(atob(stream)) ?? []) as DashboardAccessType[];
                const lookUpAccess = accessList.find(access => (access.iss === issuer && access.sub === workspace.id));

                if (!!lookUpAccess) {
                    if (!lookUpAccess.iss || (Number(lookUpAccess.exp) > Math.floor(Date.now() / 1000)))
                        setWithPublicAccess(true);
                    if (!lookUpAccess.iss || !!lookUpAccess.exp)
                        setCheckedAccess(true);
                }
            }

            setLoaded(true);
            clearTimeout(timerRef.current);
        }, 500);
    }, []);

    const handleConfirmAccess = useCallback((email: string) => {
        timerRef.current = setTimeout(() => {
            const stream = storage.getItem('config.workspace-public');

            if (!!stream) {
                const accessList = (JSON.parse(atob(stream)) ?? []) as DashboardAccessType[];
                const lookUpAccess = accessList.find(access => (access.iss === email && access.sub === workspace?.id));

                if (!!lookUpAccess) {
                    lookUpAccess.exp = Math.floor(Date.now() / 1000) + (24 * 60 * 60);
                    storage.setItem('config.workspace-public', btoa(JSON.stringify(accessList)));
                    setWithPublicAccess(true);
                }
                setCheckedAccess(true);
            }
            clearTimeout(timerRef.current);
        }, 1000);
    }, [workspace]);

    useEffect(() => {
        setDashboard(null);
        setPublicView(sharedDashboard || publicDashboard || false);
        setShared(sharedDashboard || false);

        return () => {
            setDashboard(null);
        }
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const dashboardId = params.dashboardId;
        const from = getURLFrom();

        if (!initialLoading && dashboardId) {
            ((publicDashboard || sharedDashboard)
                ? getPublicDashboardById(dashboardId)
                : getDashboardById(dashboardId))
                .then((dashboardData) => {
                    if (!dashboardData) return;

                    setDashboard(dashboardData);
                    if ((!!from && from.origin === 'workspaces' && !!from.originId)
                        && dashboardWorkspaces(dashboardData!)?.includes(from.originId)) {
                        if (publicDashboard || sharedDashboard) {
                            getPublicWorkspaceFunc({
                                id: from.originId,
                                group: getURLFrom()?.originGroup || userGroup,
                            }).then((data) => {
                                setWorkspace(data?.workspace || null);
                                if (!!data?.workspace) {
                                    handleStoreAccess(data?.workspace);
                                    handleVerifyAccess(data?.workspace);
                                } else {
                                    setLoaded(true);
                                }
                            });
                        } else {
                            const lookUpWorkspace = workspaces.find(workspace => workspace.id === from.originId);

                            if (!!lookUpWorkspace)
                                setWorkspace(prev => !prev ? lookUpWorkspace : prev);
                            setLoaded(true);
                        }
                    } else {
                        if (publicDashboard || sharedDashboard) {
                            setWithPublicAccess(true);
                            setCheckedAccess(true);
                        }
                        setLoaded(true);
                    }
                });
        }
    // eslint-disable-next-line
    }, [params.dashboardId, publicDashboard, initialLoading]);

    if (!loaded)
        return (<FallbackLoading />);

    if (isInvalidPublicDashboard)
        return (<PublicDashboardNotFound />);

    if (isValidatingPublicDashboard)
        return (<DashboardAllowAccess invalid={checkedAccess && !hasPublicAccess} onConfirm={handleConfirmAccess} />);

    return (
        <Paper className={classes.container}>
            {!queriesLoaded ? ( <DashboardLoading /> ): (<>
                <Container maxWidth="xl">
                    <Box mb={2}>
                        <Link className={classes.link}
                            onClick={redirectFromDashboard}
                            underline="none">
                            <ArrowBack sx={{ fontSize: 20 }} />
                            {'Back to Home'}
                        </Link>
                    </Box>
                    <CompanyHeader />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <Stack spacing={3} sx={{ width: { xs: '100%', md: 350 } }}>
                            <CompanyDetailsCard />
                            <TeamMembersCard />
                        </Stack>
                        <Box flex={1}>
                            <TimelineCard dashboard={dashboard} />
                        </Box>
                    </Stack>
                </Container>
            </>)}
        </Paper>
    );
};

export default DashboardDetailsPageV2;